import { useReactiveVar } from '@apollo/client'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import {
    promoteDemoteTemplateData,
    reactiveWorkspaceActions,
    setReactiveWorkspaceActions,
} from 'api/graphQL/reactiveVars/workspaceActions'
import {
    ParagraphAction,
    PromoteDemoteRequirements,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as TextVariants } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { insertPillTextUOM } from 'pages/ProjectsPage/WorkspacePage/components/PropertyEditor/propertyEditorUtils'
import React, { useMemo } from 'react'
import { areEq } from 'shared/utils/localStrorageUtils'
import { useShallowSelector } from '../Workspace2Store'
import { usePromoteDemote } from './usePromoteDemote'

export const useEditorActionBarHandler = () => {
    const { contentTeamFromStudioPage } = useShallowSelector((s) => ({
        contentTeamFromStudioPage: s.isContentTeamFromStudioPage,
    }))

    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const {
        isAddSpecifierNote,
        isInsertPill,
        isInsertText,
        isInsertUOM,
        insertUomPill,
        insertPillRequirements,
        addNoteSpecifier,
        addNoteSpecifierRequirements,
    } = useReactiveVar(reactiveWorkspaceActions)
    // destructuring requirements to create a new pill, text, UOM
    const {
        textWithPill,
        insertPillIndex,
        variant,
        fieldState,
        template,
        setFieldState,
        changeType,
        element,
        event,
        value,
        saveChanges,
        setProperty,
        titleText,
    } = insertPillRequirements

    const disableInsertPillTextUOMButtons = React.useCallback(
        () =>
            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                isInsertUOM: false,
                isAddSpecifierNote: false,
            }),
        [setReactiveWorkspaceActions]
    )

    const handleInsertPill = () => {
        if (isInsertPill) {
            try {
                setReactiveWorkspaceActions({
                    isInsertPill: false,
                    isInsertText: false,
                    isInsertUOM: true,
                    isAddSpecifierNote: false,
                    insertPillRequirements: {
                        ...insertPillRequirements,
                        variant: TextVariants.uomInPill,
                    },
                })

                insertPillTextUOM({
                    pillType: TextVariants.newPill,
                    insertPillIndex: insertPillIndex ?? 0,
                    changeType,
                    element,
                    event: event ?? undefined,
                    fieldState,
                    setFieldState,
                    value,
                    variant,
                    saveChanges,
                    setProperty,
                    template,
                    titleText,
                })
            } catch (error) {
                // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
                disableInsertPillTextUOMButtons()
            }
        }
    }

    const handleInsertText = () => {
        if (isInsertText) {
            try {
                disableInsertPillTextUOMButtons()

                insertPillTextUOM({
                    pillType: TextVariants.newReplacement,
                    insertPillIndex: insertPillIndex ?? 0,
                    changeType,
                    element,
                    event: event ?? undefined,
                    fieldState,
                    setFieldState,
                    value,
                    variant,
                    saveChanges,
                    setProperty,
                    template,
                    titleText,
                })
            } catch (error) {
                // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
                disableInsertPillTextUOMButtons()
            }
        }
    }

    const handleInsertUOM = () => {
        const isUOMVariant =
            areEq(variant, TextVariants.newPill) ||
            areEq(variant, TextVariants.uomInPill)

        try {
            if (isInsertUOM) {
                // variant is uomInPill/newPill? then, use "insertUomPill" handler
                if (insertUomPill && isUOMVariant) {
                    insertUomPill(insertPillIndex, textWithPill)
                } else {
                    // otherwise, use "insertPill" handler (implemented inside insertPillTextUOM method)
                    insertPillTextUOM({
                        pillType: TextVariants.newUOM,
                        insertPillIndex: insertPillIndex ?? 0,
                        changeType,
                        element,
                        event: event ?? undefined,
                        fieldState,
                        setFieldState,
                        value,
                        variant,
                        saveChanges,
                        setProperty,
                        template,
                        titleText,
                    })
                }
            }

            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                // enable "Insert Pill" button when UOM is inserted in pill
                isInsertUOM: isUOMVariant,
                isAddSpecifierNote: false,
                insertPillRequirements: {
                    ...insertPillRequirements,
                    // use to signify that the pill is created and can insert UOM in it, otherwise 'undefined'
                    variant: areEq(variant, TextVariants.uomInPill)
                        ? TextVariants.uomInPill
                        : undefined,
                },
            })
        } catch (error) {
            // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
            disableInsertPillTextUOMButtons()
        }
    }

    const handleAddSpecifierNote = () => {
        if (isAddSpecifierNote) {
            try {
                addNoteSpecifier?.(
                    addNoteSpecifierRequirements?.selectedTemplate
                )
            } catch (error) {
                // this is in-place so just if anything unexpected happens it won't crash and freeze the whole site.
                // error message will be handle by global snackbar on 'addNoteSpecifier' handler
            } finally {
                disableInsertPillTextUOMButtons()
            }
        }
    }
    const { promoteDemoteWorkspaceTemplateData } = useReactiveVar(
        promoteDemoteTemplateData
    )

    const {
        activeItem: contentItemToPromoteDemote,
        canPromote: isPromoteEnabled,
        canDemote: isDemoteEnabled,
    } = usePromoteDemote()

    // validate workspace action bar copy button if enabled or disabled
    const isCopyProductTypeEnabled = useMemo(() => {
        return (
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE' &&
            !contentTeamFromStudioPage &&
            selectedProjectElementNode?.isInProject
        )
    }, [contentTeamFromStudioPage, selectedProjectElementNode])

    // useEffect to trigger the promote/demote button clicked from the workspace action bar
    const handleParagraphPromoteDemote = React.useCallback(
        (action: ParagraphAction) => {
            if (action === 'Promote' && !isPromoteEnabled) return
            if (action === 'Demote' && !isDemoteEnabled) return

            const promoteDemoteContent: PromoteDemoteRequirements = {
                content: promoteDemoteWorkspaceTemplateData,
                action,
            }
            // TODO: implement mutation here
        },
        [promoteDemoteWorkspaceTemplateData, contentItemToPromoteDemote]
    )

    return {
        handleInsertPill,
        handleInsertText,
        handleInsertUOM,
        handleAddSpecifierNote,
        handleParagraphPromoteDemote,
        isDemoteEnabled,
        isPromoteEnabled,
        isCopyProductTypeEnabled,
    }
}
